import { Box } from '@mui/material'
import { styled } from '@mui/material/styles'
import SettingsIcon from '@mui/icons-material/Settings'
import { createElement, FC, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { MenuItemLink, MenuProps, ResourceDefinition, useResourceDefinitions, useSidebarState, useTranslate } from 'react-admin'
import useAutoLogout from '../hooks/useAutoLogout'
import SubMenu, { SubMenuItemLink } from './SubMenu'
import { border, themeColor } from './themes'

interface ResourceOptions {
  label?: string
  /** Resources with `submenu: 'settings'` are grouped under "Instellingen". */
  submenu?: string
}

const getOptions = (resource: ResourceDefinition): ResourceOptions =>
  (resource.options ?? {}) as ResourceOptions

/**
 * Sidebar like xxllnc.ai:
 *
 *   ▣ Certificaten
 *   ⚙ Instellingen   ⌄
 *   │  Gemeenten
 *   │  Organisaties      (only with the organization scope)
 *
 * Which resources exist is decided by `resourcesForScopes`; this only groups them.
 */
const Menu: FC<MenuProps> = ({ dense = false }) => {
  const translate = useTranslate()
  const location = useLocation()
  const [open] = useSidebarState()
  const definitions = useResourceDefinitions()
  const resources = Object.values(definitions).filter(resource => resource.hasList && resource.icon)
  const topLevel = resources.filter(resource => !getOptions(resource).submenu)
  const settings = resources.filter(resource => getOptions(resource).submenu === 'settings')
  const isActive = (name: string) => location.pathname === `/${name}` || location.pathname.startsWith(`/${name}/`)
  const settingsActive = settings.some(resource => isActive(resource.name))
  // The settings group starts open on a settings route (deep link, reload).
  const [settingsOpen, setSettingsOpen] = useState(settingsActive)

  useAutoLogout()

  const label = (resource: ResourceDefinition) => translate(getOptions(resource).label ?? resource.name)

  return (
    <StyledBox mt={1} className={classes.root}>
      <div className={classes.spacer}>&nbsp;</div>
      {topLevel.map(resource => (
        <StyledMenuItemLink
          className={classes.item}
          dense={dense}
          key={resource.name}
          primaryText={label(resource)}
          sidebarIsOpen={!open}
          to={`/${resource.name}`}
          leftIcon={createElement(resource.icon)}
        />
      ))}
      {settings.length > 0 && (
        <SubMenu
          handleToggle={() => setSettingsOpen(value => !value)}
          icon={<SettingsIcon sx={{ fontSize: 20 }} />}
          isOpen={settingsOpen}
          active={settingsActive}
          sidebarIsOpen={!open}
          name="submenu.settings"
          dense={dense}
        >
          {settings.map(resource => (
            <SubMenuItemLink
              dense={dense}
              key={resource.name}
              primaryText={label(resource)}
              sidebarIsOpen={!open}
              to={`/${resource.name}`}
            />
          ))}
        </SubMenu>
      )}
    </StyledBox>
  )
}

const PREFIX = 'xxllncMenu'

const classes = {
  root: `${PREFIX}-root`,
  item: `${PREFIX}-item`,
  spacer: `${PREFIX}-spacer`,
}

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.root}`]: {
    overflow: 'hidden',
  },
  [`& .${classes.item}`]: {
    padding: '10px 12px',
    color: themeColor.sidebarText,
    fontSize: 14,
    ['&:hover']: {
      [theme.breakpoints.up('sm')]: {
        backgroundColor: themeColor.sidebarHover,
        color: themeColor.sidebarTextHover,
      },
    },
  },
  [`& .${classes.spacer}`]: {
    height: '64px',
  },
}))

const StyledMenuItemLink = styled(MenuItemLink)(() => ({
  'color': themeColor.sidebarText,
  'borderRadius': border.radius.sm,
  'transition': 'all 0.15s ease',
  '&.RaMenuItemLink-active': {
    backgroundColor: themeColor.sidebarActive,
    color: themeColor.sidebarActiveText,
    fontWeight: 600,
  },
  '& .MuiSvgIcon-root': {
    color: themeColor.sidebarText + ' !important',
    fontSize: '20px !important',
  },
  '& .MuiListItemIcon-root': {
    color: themeColor.sidebarText + ' !important',
    minWidth: '32px',
  },
  '&:hover .MuiSvgIcon-root, &:hover .MuiListItemIcon-root': {
    color: themeColor.sidebarTextHover + ' !important',
  },
  '&.RaMenuItemLink-active .MuiSvgIcon-root, &.RaMenuItemLink-active .MuiListItemIcon-root': {
    color: themeColor.sidebarActiveText + ' !important',
  },
}))

export default Menu
